import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import { DateRangePicker } from 'react-date-range/dist'
import { vi } from 'react-date-range/dist/locale'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { languages } from '../../../configs/language'
import { FORMAT, formatDateTime } from '../../helpers/datetime'
import EventEmitter, {
  BACK_MONTH_DATE_PICKER,
  NEXT_MONTH_DATE_PICKER,
  SET_FROM_DATE,
  SET_TO_DATE,
} from '../../helpers/EventEmitter'
import { UseTimeZone } from '../hooks/useTimeZone'
import { Element } from '../html/element'
import { Icon } from '../html/icon'
import { ButtonSubmit } from './ButtonSubmit'
import { enMonths, viMonths } from './constants'
import { add0, convertResultDate } from './helper'
import classes from './index.module.scss'

const DateRange = ({
  onSubmit,
  defaultStartDate,
  defaultEndDate,
  keyInput,
  minDate,
  maxDate,
}) => {
  const timezone = UseTimeZone()

  const locale = useSelector((state) => state.i18n.locale)

  const defaultStartDateConverted = formatDateTime(
    defaultStartDate,
    FORMAT.DATE_PICKER,
    locale,
    timezone,
  )

  const defaultEndDateConverted = formatDateTime(
    defaultEndDate,
    FORMAT.DATE_PICKER,
    locale,
    timezone,
  )

  const defaultStateDate = useRef([
    {
      startDate: new Date(defaultStartDateConverted),
      endDate: new Date(defaultEndDateConverted),
      key: keyInput,
    },
  ])

  const defaultStateMonthYear = useRef({
    month: new Date(defaultStartDateConverted).getMonth(),
    year: new Date(defaultEndDateConverted).getFullYear(),
  })

  const [stateDate, setStateDate] = useState(defaultStateDate.current)

  const [stateMonthYear, setStateMonthYear] = useState(
    defaultStateMonthYear.current,
  )

  useEffect(() => {
    const changeDateFromInput = (key) => (date) => {
      setStateDate([{ ...stateDate[0], [key]: date }])
      if (key === 'startDate') {
        setStateMonthYear({ month: date.getMonth(), year: date.getFullYear() })
      }
    }

    const changeDateFromFromInput = changeDateFromInput('startDate')
    const changeDateToFromInput = changeDateFromInput('endDate')

    EventEmitter.subscribe(SET_FROM_DATE, changeDateFromFromInput)
    EventEmitter.subscribe(SET_TO_DATE, changeDateToFromInput)

    return () => {
      EventEmitter.unsubscribe(SET_FROM_DATE, changeDateFromFromInput)
      EventEmitter.unsubscribe(SET_TO_DATE, changeDateToFromInput)
    }
  }, [stateDate])

  const nextMonth = () => EventEmitter.dispatch(NEXT_MONTH_DATE_PICKER)

  const backMonth = () => EventEmitter.dispatch(BACK_MONTH_DATE_PICKER)

  const onShownDateChange = (newDate) =>
    setStateMonthYear({
      month: newDate.getMonth(),
      year: newDate.getFullYear(),
    })

  const resetDefault = () => {
    setStateDate(defaultStateDate.current)
    setStateMonthYear(defaultStateMonthYear.current)
  }

  const getTitleDate = (date) =>
    add0(date.getDate()) +
    ' ' +
    months[date.getMonth()] +
    ' ' +
    date.getFullYear()

  const submit = () => {
    onSubmit({
      startDate: convertResultDate(stateDate[0].startDate, timezone),
      endDate: convertResultDate(stateDate[0].endDate, timezone, false),
    })
  }

  const months = locale === languages.vi ? viMonths : enMonths

  const isLastMonth = stateMonthYear.month === 11

  return (
    <Element
      style={{ backgroundColor: '#FFFFFF' }}
      className={classes.calendarContainer}
    >
      <div className={classes.header}>
        <div className={classes.title}>
          <Element style={{ fontSize: 20 }}>
            <Translate value="common.datePicker.CUSTOM_DATE_RANGE" />
          </Element>
        </div>
        <div className="dFlex alignItemsCenter">
          <div className={classes.previewBox}>
            <Icon className="icon-calendar" style={{ fontSize: 13 }} />
            <Element style={{ fontSize: 12 }}>
              {getTitleDate(stateDate[0].startDate)}
            </Element>
          </div>
          <div className={classes.previewBox}>
            <Icon className="icon-calendar" style={{ fontSize: 13 }} />
            <Element style={{ fontSize: 12 }}>
              {getTitleDate(stateDate[0].endDate)}
            </Element>
          </div>
          <div onClick={resetDefault} className={classes.reset}>
            <Element style={{ fontSize: 12 }}>
              <Translate value="common.datePicker.RESET" />
            </Element>
          </div>
        </div>
      </div>
      <div className={classes.monthYearContainer}>
        <div className={classes.monthYear}>
          <Icon
            className="icon-arrow-left"
            style={{ fontSize: 10 }}
            onClick={backMonth}
          />
          <Element style={{ fontSize: 14 }}>
            {months[stateMonthYear.month]} {stateMonthYear.year}
          </Element>
        </div>
        <div className={classes.monthYear}>
          <Element style={{ fontSize: 14 }}>
            {months[isLastMonth ? 0 : stateMonthYear.month + 1]}{' '}
            {isLastMonth ? stateMonthYear.year + 1 : stateMonthYear.year}
          </Element>
          <Icon
            className="icon-arrow-right"
            style={{ fontSize: 10 }}
            onClick={nextMonth}
          />
        </div>
      </div>
      <div className={classes.dateRangeContainer}>
        <DateRangePicker
          locale={locale === languages.vi ? vi : undefined}
          onChange={(item) => setStateDate([item[keyInput]])}
          moveRangeOnFirstSelection={false}
          months={2}
          ranges={stateDate}
          direction="horizontal"
          staticRanges={[]}
          inputRanges={[]}
          showDateDisplay={false}
          showMonthArrow={false}
          showMonthAndYearPickers={false}
          weekStartsOn={1}
          onShownDateChange={onShownDateChange}
          minDate={minDate ? new Date(minDate) : undefined}
          maxDate={maxDate ? new Date(maxDate) : undefined}
        />
      </div>
      <div className={classes.buttonContainer}>
        <ButtonSubmit submit={submit} />
      </div>
    </Element>
  )
}

DateRange.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  defaultStartDate: PropTypes.any,
  defaultEndDate: PropTypes.any,
  keyInput: PropTypes.string,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
}

DateRange.defaultProps = {
  defaultStartDate: new Date(),
  defaultEndDate: new Date(),
  keyInput: 'selection',
}

export default DateRange
